<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Bot, Clock3, Cpu, Database, MessageSquareText, Radio, RefreshCw, Volume2 } from "lucide-vue-next";
import { api } from "../services/api";
import type { AgentSummary, Device, Health, Invocation } from "../types";

const props = defineProps<{ health?: Health; agents: AgentSummary[] }>();
const invocations = ref<Invocation[]>([]);
const devices = ref<Device[]>([]);
const loading = ref(false);
const error = ref("");

const serviceRows = computed(() => [
  { key: "database", label: "PostgreSQL", icon: Database, ready: props.health?.services.database },
  { key: "ark", label: "方舟问答", icon: MessageSquareText, ready: props.health?.services.ark },
  { key: "realtime", label: "实时语音", icon: Radio, ready: props.health?.services.realtime },
  { key: "tts", label: "语音合成", icon: Volume2, ready: props.health?.services.tts },
  { key: "mqtt", label: "MQTT", icon: Cpu, ready: props.health?.services.mqtt },
]);

async function load(): Promise<void> {
  loading.value = true;
  error.value = "";
  const [logs, deviceList] = await Promise.allSettled([api.invocations(12), api.devices()]);
  if (logs.status === "fulfilled") invocations.value = logs.value;
  if (deviceList.status === "fulfilled") devices.value = deviceList.value;
  if (logs.status === "rejected") error.value = logs.reason instanceof Error ? logs.reason.message : "调用记录加载失败";
  loading.value = false;
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(value));
}

onMounted(load);
</script>

<template>
  <div class="view-stack">
    <section class="metric-grid">
      <article class="metric-block">
        <div class="metric-icon green"><Bot :size="20" /></div>
        <span>可用智能体</span>
        <strong>{{ agents.length }}</strong>
      </article>
      <article class="metric-block">
        <div class="metric-icon blue"><Cpu :size="20" /></div>
        <span>已发现设备</span>
        <strong>{{ devices.length }}</strong>
      </article>
      <article class="metric-block">
        <div class="metric-icon amber"><MessageSquareText :size="20" /></div>
        <span>最近调用</span>
        <strong>{{ invocations.length }}</strong>
      </article>
      <article class="metric-block">
        <div class="metric-icon neutral"><Clock3 :size="20" /></div>
        <span>在线设备</span>
        <strong>{{ devices.filter((device) => device.status === 'ONLINE').length }}</strong>
      </article>
    </section>

    <section class="split-layout dashboard-split">
      <div class="surface-section">
        <div class="section-header">
          <div><span class="eyebrow">服务</span><h2>运行状态</h2></div>
          <button class="icon-button compact" type="button" title="刷新" :disabled="loading" @click="load"><RefreshCw :size="16" /></button>
        </div>
        <div class="service-list">
          <div v-for="service in serviceRows" :key="service.key" class="service-row">
            <component :is="service.icon" :size="18" />
            <span>{{ service.label }}</span>
            <strong :class="service.ready ? 'text-ok' : 'text-muted'">{{ service.ready ? "可用" : "未配置" }}</strong>
          </div>
        </div>
      </div>

      <div class="surface-section">
        <div class="section-header"><div><span class="eyebrow">审计</span><h2>最近调用</h2></div></div>
        <div v-if="error" class="empty-state error-state">{{ error }}</div>
        <div v-else-if="!invocations.length" class="empty-state">暂无调用记录</div>
        <div v-else class="activity-list">
          <div v-for="item in invocations" :key="item.id" class="activity-row">
            <span :class="['status-dot', item.status === 'completed' ? 'ok' : 'bad']" />
            <div>
              <strong>{{ item.agent.name }}</strong>
              <span>{{ item.capability }} · {{ item.clientApp.name }}</span>
            </div>
            <div class="activity-meta">
              <strong>{{ item.durationMs ? `${item.durationMs} ms` : '—' }}</strong>
              <span>{{ formatTime(item.createdAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
